import { Query } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { useEffect } from 'react';
import fetchTaxStatements from 'services/graphql/queries/FetchTaxStatements';
import GetDocument from 'services/graphql/queries/GetDocument';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useQuery } from 'urql';

interface TaxStatements {
  year?: string;
  documentId?: string;
}
const useTaxStatements = ({ year, documentId = '' }: TaxStatements) => {
  const router = useRouter();
  const [
    { data: taxStatementsData, fetching: loading, error },
    refetchTaxStatements,
  ] = useQuery<Query>({
    query: fetchTaxStatements,
    requestPolicy: 'cache-and-network',
  });

  const [{ data: documentDetails }, refetchDocument] = useQuery<Query>({
    query: GetDocument,
    pause: true,
    variables: { id: documentId },
  });

  useEffect(() => {
    if (documentId !== '') {
      refetchDocument({ id: documentId });
    }
  }, [documentId, refetchDocument]);

  const refresh = () => {
    refetchTaxStatements({ requestPolicy: 'network-only' });
  };
  const cancel = () => {
    router.back();
  };

  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();

  const responseError = useFormattedCombinedError(error);

  const buttons = {
    primaryButton: {
      title: retryButtonTitle,
      onPress: refresh,
    },
    secondaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel = error
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError?.title ?? defaultTitle,
        messageBody: responseError?.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  const taxStatementsDetails = taxStatementsData?.taxStatements;

  const yearlyStatementsData = taxStatementsDetails?.flatMap((statements) => {
    return year === statements?.year?.toString() ? statements?.statements : [];
  });

  return {
    taxStatementsDetails,
    yearlyStatementsData,
    errorModel,
    loading,
    refresh,
    documentDetails,
  };
};

export default useTaxStatements;
